// apps/client/src/hooks/useRolling3RunWindow.ts
// MIGRATED (2025-11-26): Now uses BigQuery via getRolling3Run Cloud Function
// Previous: Queried Firestore upload_aggregates collection with onSnapshot (deprecated)
// Current: Queries BigQuery upload_runs table via Cloud Function
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useDashboardQuery } from './useDashboardQuery';
import { formatCompactCurrency } from '../utils/formatters';

/**
 * Upload aggregate data structure (matching uploadAggregates.ts)
 */
export interface UploadAggregate {
  uploadId: string;
  companyId: string;
  templateId: string;
  yearMonth: string; // "2025-09"
  uploadedAt: Timestamp;
  runNumber: number;
  filename: string;

  upfrontGross: number;
  upfrontAdjustments: number;
  upfrontNet: number;

  trailGross: number;
  trailAdjustments: number;
  trailNet: number;

  totalLoanAmount: number;
  lineOfTrail: number;
  recordCount: number;

  // Breakdown by broker (optional, for detailed analysis)
  byBroker?: Record<string, {
    brokerDisplay: string;
    brokerId: string;
    upfrontNet: number;
    trailNet: number;
    totalLoanAmount: number;  // ✅ OFFICE DASHBOARD: Loan amount per broker
    lineOfTrail: number;
    recordCount: number;
  }>;

  // ✅ NEW: Breakdown by office (matches monthly_aggregates pattern)
  byOffice: Record<string, {
    upfrontGross: number;
    upfrontAdjustments: number;
    upfrontNet: number;
    trailGross: number;
    trailAdjustments: number;
    trailNet: number;
    totalLoanAmount: number;
    lineOfTrail: number;
    recordCount: number;
  }>;

  processingVersion: number;
}

/**
 * Rolling 3-run window result
 */
export interface Rolling3RunWindow {
  // Totals across last 3 runs
  totalLineOfTrail: number;
  totalLoanBook: number;
  totalUpfrontNet: number;
  totalTrailNet: number;
  totalRecords: number;

  // Individual runs (most recent first) - can be UploadAggregate[] or MonthlyAggregate[]
  runs: any[];

  // Summary for display
  runsSummary: string; // e.g., "Run 1 (2025-10), Run 3 (2025-09), Run 2 (2025-09)" OR "FY 2024/2025"

  // Loading state
  loading: boolean;
}

/**
 * Parameters for useRolling3RunWindow hook
 */
export interface UseRolling3RunWindowParams {
  companyId: string | undefined;
  officeId?: string;
  templateId?: string;
  brokerId?: string;
  lender?: string;
  runCount?: number;
  // Option A: Year-based mode (uses monthly_aggregates instead of upload_aggregates)
  yearMode?: {
    year: number;
    yearType: 'calendar' | 'financial';
  };
  // Option B: Month-range based (new - for relative periods)
  monthRange?: {
    startMonth: string;  // YYYY-MM format
    endMonth: string;    // YYYY-MM format
  };
  // Return broker breakdown for activity status filtering
  includeByBroker?: boolean;
  entityCode?: string;
}

// Broker breakdown type (matches getRolling3Run.ts BrokerBreakdown)
interface BrokerBreakdown {
  brokerDisplay: string;
  totalLoanAmount: number;
  lineOfTrail: number;
  upfrontNet: number;
  trailNet: number;
  recordCount: number;
}

// Cloud Function response type (matches getRolling3Run.ts)
interface Rolling3RunResponse {
  totalLineOfTrail: number;
  totalLoanBook: number;
  totalUpfrontNet: number;
  totalTrailNet: number;
  totalRecords: number;
  months: Array<{
    month: string;          // Run name (e.g., "RUN 1")
    loanBook: number;       // In cents
    lineOfTrail: number;    // Count of loans
    upfrontNet: number;     // In cents
    trailNet: number;       // In cents
    recordCount: number;
    byBroker?: Record<string, BrokerBreakdown>;  // Broker breakdown for activity filtering
  }>;
}

/**
 * Custom hook to get loan book and line of trail data
 *
 * MIGRATED (2025-11-26): Now uses BigQuery via getRolling3Run Cloud Function
 * Previous: Queried Firestore upload_aggregates collection with onSnapshot (deprecated)
 *
 * TWO MODES:
 * 1. Rolling Window Mode (default): Queries last N uploads from BigQuery upload_runs
 * 2. Year-Based Mode: Queries upload_runs for a specific year (calendar or financial)
 *
 * @param params - Hook parameters
 */
export function useRolling3RunWindow(params: UseRolling3RunWindowParams): Rolling3RunWindow {
  const { companyId, officeId, templateId, brokerId, lender, yearMode, monthRange, includeByBroker, entityCode } = params;

  // Either yearMode or monthRange is required for the Cloud Function; without one
  // the query stays disabled and the zeroed window below is returned (matches the
  // pre-migration hook's early-return, which cleared loading and left totals at 0).
  const enabled = !!(yearMode || monthRange);

  const { data, loading } = useDashboardQuery<Rolling3RunResponse>(
    'getRolling3Run',
    {
      companyId: companyId ?? '',
      officeId,
      templateId,
      brokerId,
      lender,
      ...(monthRange
        ? { startMonth: monthRange.startMonth, endMonth: monthRange.endMonth }
        : yearMode
          ? { year: yearMode.year, yearType: yearMode.yearType }
          : {}),
      includeByBroker,
      entityCode,
    },
    { enabled }
  );

  // Resilience (bespoke-hook parity): once a window has loaded, never re-zero
  // the Loan Book / LOT headlines because `data` went undefined, whether from
  // a transient getRolling3Run error OR a dropped/garbage-collected query
  // result (two simultaneous windows of the same callable in legacy mode). Hold
  // the last-good response and fall back to it regardless of error state; the
  // zeroed window below is reserved for the never-loaded case.
  const lastGoodRef = useRef<Rolling3RunResponse | undefined>(undefined);
  if (data) {
    lastGoodRef.current = data;
  }
  const effectiveData = data ?? lastGoodRef.current;

  // Transform response to match existing Rolling3RunWindow interface. When the
  // query is disabled or has never loaded, return the zeroed window verbatim.
  return useMemo<Rolling3RunWindow>(() => {
    if (!enabled || !effectiveData) {
      return {
        totalLineOfTrail: 0,
        totalLoanBook: 0,
        totalUpfrontNet: 0,
        totalTrailNet: 0,
        totalRecords: 0,
        runs: [],
        runsSummary: '',
        loading,
      };
    }

    // Generate summary string for display
    const runsSummary = effectiveData.months
      .map(run => run.month)
      .join(', ');

    return {
      totalLineOfTrail: effectiveData.totalLineOfTrail,
      totalLoanBook: effectiveData.totalLoanBook,
      totalUpfrontNet: effectiveData.totalUpfrontNet,
      totalTrailNet: effectiveData.totalTrailNet,
      totalRecords: effectiveData.totalRecords,
      runs: effectiveData.months, // Runs data from BigQuery
      runsSummary,
      loading,
    };
  }, [enabled, effectiveData, loading]);
}

/**
 * ⚠️ DEPRECATED: Use formatLoanBook from '../utils/formatters' instead
 *
 * Helper: Format loan book value (in cents) for display
 * @deprecated Import from '../utils/formatters' instead
 */
export function formatLoanBook(value: number): string {
  return formatCompactCurrency(value);
}

// NOTE: formatCurrency and formatNumber now imported from utils/formatters (removed duplicates)